import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    IconButton,
    Tooltip,
    Skeleton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getValuationByTicker, getFactorRatings } from "../../../api/stocks_api";
import { getRecentlyViewed } from "../../../api/api";
import RecentlyViewedWidget from "../Widgets/recentlyViewedBox";
import { Download, Info, TrendingUp } from "lucide-react";
import { fmtL } from "../../../utils/currency";

/* -------------------- GRADE COLOR MAP -------------------- */
const gradeColor = (grade) => {
    if (!grade) return "#64748b";
    if (grade.startsWith("A")) return "#10b981";
    if (grade.startsWith("B")) return "#60a5fa";
    if (grade.startsWith("C")) return "#f59e0b";
    if (grade.startsWith("D")) return "#ef4444";
    return "#dc2626"; // F - darker red
};

const gradeBackground = (grade) => {
    const map = {
        "A+": "var(--color-grade-a-plus)",
        "A": "var(--color-grade-a)",
        "A-": "var(--color-grade-a-minus)",

        "B+": "var(--color-grade-b-plus)",
        "B": "var(--color-grade-b)",
        "B-": "var(--color-grade-b-minus)",

        "C+": "var(--color-grade-c-plus)",
        "C": "var(--color-grade-c)",
        "C-": "var(--color-grade-c-minus)",

        "D+": "var(--color-grade-d-plus)",
        "D": "var(--color-grade-d)",
        "D-": "var(--color-grade-d-minus)",

        "E": "var(--color-grade-e)",

        "F": "var(--color-grade-f)",
    };

    return map[grade] ?? "var(--color-grade-unknown)";
};

/* -------------------- METRIC LABEL MAP -------------------- */
const METRIC_LABELS = {
    fcf_yield: "FCF Yield",
    earnings_yield: "Earnings Yield",
    ev_ebitda: "EV / EBITDA",
    price_sales: "Price / Sales",
};

const METRIC_DESCRIPTIONS = {
    fcf_yield: "Free Cash Flow relative to market cap",
    earnings_yield: "Earnings relative to market cap (inverse of P/E)",
    ev_ebitda: "Enterprise Value to EBITDA multiple",
    price_sales: "Market cap relative to revenue",
};

/* -------------------- TAB INFO -------------------- */
const TAB_INFO = {
    0: "Composite valuation grade based on fundamental metrics",
    1: "Price-to-earnings analysis and historical trends",
    2: "Debt levels, equity structure, and leverage ratios",
};

/* -------------------- LOWER IS BETTER METRICS -------------------- */
const LOWER_IS_BETTER = ["ev_ebitda", "price_sales"];

const getDisplayPercentile = (key, percentile) => {
    if (percentile == null) return null;
    return LOWER_IS_BETTER.includes(key) ? 100 - percentile : percentile;
};

/* -------------------- TABLE COLUMNS CONFIG -------------------- */
const getTableColumns = (ticker) => [
    {
        key: "metric",
        label: "Metric",
        align: "left",
        sticky: true,
        minWidth: 180,
    },
    {
        key: "grade",
        label: "FA Grade",
        align: "center",
        minWidth: 90,
    },
    {
        key: "current_value",
        label: `${ticker} Value`,
        align: "right",
        minWidth: 120,
        highlight: true,
    },
    {
        key: "percentile",
        label: "Percentile",
        align: "right",
        minWidth: 110,
    },
    {
        key: "score",
        label: "FA Score",
        align: "right",
        minWidth: 100,
    },
];

/* -------------------- TABLE SKELETON -------------------- */
const TableSkeleton = () => (
    <Box sx={{ p: 3 }}>
        {[...Array(4)].map((_, idx) => (
            <Box
                key={idx}
                sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    pb: 2,
                    borderBottom: idx < 3 ? "1px solid var(--color-border-primary)" : "none",
                }}
            >
                <Skeleton variant="text" width="30%" height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": {
                        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                    }
                }} />
                <Skeleton variant="rounded" width={80} height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": {
                        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                    }
                }} />
                <Skeleton variant="text" width="15%" height={32} sx={{
                    ml: "auto",
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": {
                        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                    }
                }} />
                <Skeleton variant="text" width="15%" height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": {
                        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                    }
                }} />
                <Skeleton variant="text" width="15%" height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": {
                        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                    }
                }} />
            </Box>
        ))}
    </Box>
);

const Valuation = ({ stock }) => {
    const { ticker } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [valuation, setValuation] = useState(null);
    const [factorRatings, setFactorRatings] = useState(null);
    const [tab, setTab] = useState(0);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    /* -------------------- API CALL FOR VALUATION -------------------- */
    useEffect(() => {
        if (!ticker) return;

        const loadValuation = async () => {
            setLoading(true);
            const res = await getValuationByTicker(ticker, true);

            if (res.authExpired) {
                navigate("/login");
                return;
            }

            if (res.success) {
                setValuation(res.data);
            }

            setLoading(false);
        };

        loadValuation();
    }, [ticker, navigate]);

    /* -------------------- API CALL FOR FACTOR RATINGS -------------------- */
    useEffect(() => {
        if (!ticker) return;

        const loadValuationFactor = async () => {
            setLoading(true);
            try {
                const res = await getFactorRatings(ticker, "valuation");

                if (res?.authExpired) {
                    navigate("/login");
                    return;
                }

                if (res?.success && res?.response_data && res.response_data.grade != null) {
                    setValuation(res.response_data);
                } else {
                    setValuation(null);
                }
            } catch (err) {
                console.error("Valuation factor fetch error:", err);
                setValuation(null);
            }
            setLoading(false);
        };

        loadValuationFactor();
    }, [ticker, navigate]);


    /* -------------------- RECENTLY VIEWED -------------------- */
    useEffect(() => {
        const fetchRecentlyViewed = async () => {
            setLoadingRecent(true);
            const res = await getRecentlyViewed();

            if (res.success) {
                setRecentlyViewed(
                    res.data.filter((item) => item.ticker !== stock?.ticker)
                );
            }
            setLoadingRecent(false);
        };

        if (stock?.ticker) fetchRecentlyViewed();
    }, [stock?.ticker]);

    const exportToCSV = () => {
        if (!valuation?.breakdown) return;

        const { breakdown } = valuation;
        let csv = "Metric,Grade,Value,Percentile,Score\n";

        Object.entries(breakdown).forEach(([key, data]) => {
            const metricLabel = METRIC_LABELS[key] || key;
            const value = data.raw_value?.toFixed(2) || "—";

            csv += `${metricLabel},${data.grade},${value},${data.percentile.toFixed(
                2
            )},${data.score.toFixed(2)}\n`;
        });

        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${ticker}_valuation_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const formatPercentile = (percentile) => {
        if (percentile >= 75) return { text: `${fmtL(percentile, 1)}%`, color: "#10b981" };
        if (percentile >= 50) return { text: `${fmtL(percentile, 1)}%`, color: "#60a5fa" };
        if (percentile >= 25) return { text: `${fmtL(percentile, 1)}%`, color: "#f59e0b" };
        return { text: `${fmtL(percentile, 1)}%`, color: "#ef4444" };
    };

    /* -------------------- LOADING -------------------- */
    if (loading) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: "var(--radius-xl)",
                            overflow: "hidden",
                            boxShadow: "var(--shadow-md)",
                        }}
                    >
                        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2, borderBottom: "1px solid var(--color-border-primary)" }}>
                            <Skeleton variant="text" width={200} height={32} sx={{
                                mb: 1,
                                bgcolor: "var(--color-surface-hover)",
                                "&::after": {
                                    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                                }
                            }} />
                            <Skeleton variant="text" width="60%" height={20} sx={{
                                bgcolor: "var(--color-surface-hover)",
                                "&::after": {
                                    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                                }
                            }} />
                            <Skeleton variant="rectangular" width="100%" height={48} sx={{
                                mt: 2,
                                borderRadius: "var(--radius-md)",
                                bgcolor: "var(--color-surface-hover)",
                                "&::after": {
                                    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
                                }
                            }} />
                        </Box>
                        <TableSkeleton />
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={[]} loading={true} />
                </Box>
            </Box>
        );
    }

    if (!valuation) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2, flexDirection: { xs: "column", lg: "row" } }}>
                <Box sx={{ flex: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: "var(--radius-xl)",
                            overflow: "hidden",
                            boxShadow: "var(--shadow-md)",
                            p: { xs: 3, sm: 4 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 200,
                            gap: 2,
                        }}
                    >
                        <Info size={40} style={{ color: "var(--color-text-muted)" }} />
                        <Typography sx={{ fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--color-text-primary)" }}>
                            Data not available for this ticker
                        </Typography>
                        <Typography sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", textAlign: "center", maxWidth: 400 }}>
                            Valuation factor ratings data is not available for {ticker}. This stock may not have sufficient data for Valuation analysis.
                        </Typography>
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
                </Box>
            </Box>
        );
    }

    const { grade, score, breakdown } = valuation || {};
    const safeBreakdown = breakdown || {};
    const tableColumns = getTableColumns(ticker);

    /* If valuation exists but has no usable data, show the empty state */
    if (!grade && !score && (!breakdown || Object.keys(safeBreakdown).length === 0)) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2, flexDirection: { xs: "column", lg: "row" } }}>
                <Box sx={{ flex: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: "var(--radius-xl)",
                            overflow: "hidden",
                            boxShadow: "var(--shadow-md)",
                            p: { xs: 3, sm: 4 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 200,
                            gap: 2,
                        }}
                    >
                        <Info size={40} style={{ color: "var(--color-text-muted)" }} />
                        <Typography sx={{ fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--color-text-primary)" }}>
                            Data not available for this ticker
                        </Typography>
                        <Typography sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", textAlign: "center", maxWidth: 400 }}>
                            Valuation factor ratings data is not available for {ticker}. This stock may not have sufficient data for Valuation analysis.
                        </Typography>
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                gap: 3,
                width: "100%",
                px: { xs: 2, md: 3 },
                py: 2,
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Paper
                    elevation={0}
                    sx={{
                        bgcolor: "var(--color-surface)",
                        border: "1px solid var(--color-border-primary)",
                        borderRadius: "var(--radius-xl)",
                        overflow: "hidden",
                    }}
                >
                    {/* HEADER + OVERALL RATING */}
                    <Box
                        sx={{
                            px: 3,
                            py: 3,
                            borderBottom: "1px solid var(--color-border-primary)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        {/* Left: Title + Date */}
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "1.3rem",
                                    fontWeight: 700,
                                    color: "var(--color-text-primary)",
                                }}
                            >
                                {ticker} Valuation
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "0.9rem",
                                    color: "var(--color-text-secondary)",
                                    mt: 0.5,
                                }}
                            >
                                Rating Date: {valuation?.rating_date ? new Date(valuation.rating_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </Typography>
                        </Box>

                        {/* Right: Overall Grade + FA Score */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    px: 3,
                                    py: 1,
                                    borderRadius: "var(--radius-md)",
                                    bgcolor: gradeBackground(valuation?.grade),
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        color: "#fff",
                                        fontSize: "1.1rem",
                                    }}
                                >
                                    {valuation?.grade}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: "var(--color-primary)",
                                }}
                            >
                                FA Score: {valuation?.score != null ? fmtL(valuation.score, 1) : "—"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* BREAKDOWN TABLE */}
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                        <Box component="thead">
                            <Box component="tr">
                                {["Metric", "FA Grade"].map((header) => (
                                    <Box
                                        key={header}
                                        component="th"
                                        sx={{
                                            textAlign: "left",
                                            p: 2,
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            color: "var(--color-text-secondary)",
                                            borderBottom: "1px solid var(--color-border-primary)",
                                        }}
                                    >
                                        {header}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box component="tbody">
                            {Object.entries(valuation?.breakdown || {}).map(([key, val]) => (
                                <Box
                                    component="tr"
                                    key={key}
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: "var(--color-surface-hover)",
                                        },
                                    }}
                                >
                                    {/* Metric Name */}
                                    <Box
                                        component="td"
                                        sx={{
                                            p: 2,
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {METRIC_LABELS[key] || key}
                                    </Box>

                                    {/* Grade */}
                                    <Box
                                        component="td"
                                        sx={{
                                            p: 2,
                                            borderBottom: "1px solid var(--color-border-primary)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: "var(--radius-sm)",
                                                bgcolor: gradeBackground(val.grade),
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#ffffff",
                                                }}
                                            >
                                                {val.grade}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Percentile */}
                                    {/* <Box
                                        component="td"
                                        sx={{
                                            p: 2,
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            fontWeight: 600,
                                            color: "var(--color-primary)",
                                        }}
                                    >
                                        {val.score}
                                    </Box> */}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                <RecentlyViewedWidget
                    data={recentlyViewed}
                    loading={loadingRecent}
                />
            </Box>
        </Box>
    );
};

export default Valuation;